import React from 'react';
import { Autocomplete, Box, Button, Grid, TextField, Typography, Collapse } from '@mui/material';
import { FilterList as FilterIcon, Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { acSx, STATUS_OPTIONS } from './hubUtils';

const GENDER_OPTIONS = [
  { value: '', label: 'All Genders' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

const MARK_STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'exempted', label: 'Exempted' },
];

const GRADE_OPTIONS = [
  { value: '', label: 'All Grades' },
  { value: 'A', label: 'A (Excellent)' },
  { value: 'B', label: 'B (Good)' },
  { value: 'C', label: 'C (Average)' },
  { value: 'D', label: 'D (Below Avg)' },
  { value: 'E', label: 'E (Poor)' },
  { value: 'F', label: 'F (Fail)' },
];

export default function HubFilters({
  filters, setFilters,
  classes, terms, streams, students, assessmentConfigs,
  loadingBase, loading, processing,
  onLoad, onRefresh,
}) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  const currentTerm = React.useMemo(() => terms.find((t) => t.is_current) || null, [terms]);

  // Derive unique assessment types from loaded configs
  const assessmentTypes = React.useMemo(() => {
    const seen = new Map();
    assessmentConfigs.forEach((c) => {
      const at = c.assessment_type;
      if (at && !seen.has(at.id)) seen.set(at.id, at);
    });
    return Array.from(seen.values());
  }, [assessmentConfigs]);

  const availableStreams = filters.class_id
    ? streams.filter((s) => String(s.class_id) === String(filters.class_id))
    : streams;

  const availableStudents = students.filter((s) => {
    if (filters.class_id && String(s.class_id || s.class_stream?.class_id) !== String(filters.class_id)) return false;
    if (filters.class_stream_id && String(s.class_stream_id) !== String(filters.class_stream_id)) return false;
    if (filters.gender && s.gender !== filters.gender) return false;
    return true;
  });

  const set = (key, val) => setFilters((p) => ({ ...p, [key]: val }));

  return (
    <Box mb={3} p={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <FilterIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>Filters</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" onClick={onRefresh} disabled={loadingBase || processing}>
            {loadingBase ? 'Loading...' : 'Refresh'}
          </Button>
          <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={onLoad} disabled={loading || processing || loadingBase}>
            {loading ? 'Loading...' : 'Load Data'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth size="small" type="number" label="Year"
            value={filters.academic_year}
            onChange={(e) => set('academic_year', e.target.value)}
            inputProps={{ min: 2020, max: 2100 }}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" options={terms}
            value={terms.find((t) => String(t.id) === String(filters.term_id)) || null}
            onChange={(_, v) => set('term_id', v?.id || '')}
            getOptionLabel={(o) => `${o.name || 'Term'} ${o.year || ''}`.trim()}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Term" size="small" />}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" options={classes}
            value={classes.find((c) => String(c.id) === String(filters.class_id)) || null}
            onChange={(_, v) => setFilters((p) => ({ ...p, class_id: v?.id || '', class_stream_id: '', student_id: '' }))}
            getOptionLabel={(o) => o.name || o.class_name || 'Class'}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Class" size="small" />}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" options={availableStreams}
            value={availableStreams.find((s) => String(s.id) === String(filters.class_stream_id)) || null}
            onChange={(_, v) => setFilters((p) => ({ ...p, class_stream_id: v?.id || '', student_id: '' }))}
            getOptionLabel={(o) => o.display_name || o.name || o.stream_name || 'Stream'}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" options={availableStudents}
            value={availableStudents.find((s) => String(s.id) === String(filters.student_id)) || null}
            onChange={(_, v) => set('student_id', v?.id || '')}
            getOptionLabel={(o) => o.full_name || `${o.last_name || ''} ${o.first_name || ''}`.trim() || `#${o.id}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Student (optional)" size="small" />}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" multiple
            options={assessmentTypes}
            value={assessmentTypes.filter((t) => (filters.assessment_type_ids || []).map(String).includes(String(t.id)))}
            onChange={(_, v) => set('assessment_type_ids', v.map((t) => t.id))}
            getOptionLabel={(o) => o.assessment_name || o.name || `Type #${o.id}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label="Assessment Type" size="small" />}
            limitTags={2}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" options={GENDER_OPTIONS}
            value={GENDER_OPTIONS.find((o) => o.value === filters.gender) || GENDER_OPTIONS[0]}
            onChange={(_, v) => set('gender', v?.value || '')}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(p) => <TextField {...p} label="Gender" size="small" />}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Autocomplete
            fullWidth sx={acSx} size="small" options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find((o) => o.value === filters.status) || STATUS_OPTIONS[0]}
            onChange={(_, v) => set('status', v?.value || '')}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(p) => <TextField {...p} label="RC Status" size="small" />}
          />
        </Grid>
      </Grid>

      {/* Advanced Filters */}
      {/* <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          size="small"
          onClick={() => setShowAdvanced(!showAdvanced)}
          endIcon={<ExpandMoreIcon sx={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
        </Button>
      </Box>

      <Collapse in={showAdvanced} sx={{ mt: 2 }}>
        <Grid container spacing={1.5} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Grid item xs={12}>
            <Typography variant="caption" fontWeight={600} color="textSecondary">
              MARK STATUS & GRADES
            </Typography>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Autocomplete
              fullWidth sx={acSx} size="small" options={MARK_STATUS_OPTIONS}
              value={MARK_STATUS_OPTIONS.find((o) => o.value === filters.mark_status) || MARK_STATUS_OPTIONS[0]}
              onChange={(_, v) => set('mark_status', v?.value || '')}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(p) => <TextField {...p} label="Mark Status" size="small" />}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Autocomplete
              fullWidth sx={acSx} size="small" options={GRADE_OPTIONS}
              value={GRADE_OPTIONS.find((o) => o.value === filters.grade_filter) || GRADE_OPTIONS[0]}
              onChange={(_, v) => set('grade_filter', v?.value || '')}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(p) => <TextField {...p} label="Grade Filter" size="small" />}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min Percentage"
              value={filters.min_percentage || ''}
              onChange={(e) => set('min_percentage', e.target.value)}
              inputProps={{ min: 0, max: 100, step: 5 }}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Percentage"
              value={filters.max_percentage || ''}
              onChange={(e) => set('max_percentage', e.target.value)}
              inputProps={{ min: 0, max: 100, step: 5 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setFilters({
                  academic_year: currentTerm?.year || new Date().getFullYear(),
                  term_id: currentTerm?.id || '',
                  class_id: '',
                  class_stream_id: '',
                  student_id: '',
                  gender: '',
                  status: '',
                  assessment_type_id: '',
                  mark_status: '',
                  grade_filter: '',
                  min_percentage: '',
                  max_percentage: '',
                });
              }}
            >
              Reset All Filters
            </Button>
          </Grid>
        </Grid>
      </Collapse> */}
    </Box>
  );
}
